/**
 * Every user-facing string the assistant can produce deterministically, as translation keys.
 *
 * The assistant runs server-side but talks to a person, so no handler may return English prose. A
 * deterministic reply carries a KEY (plus interpolation params); the frontend renders it with `t()`, so
 * the same reply works in English and Swedish. Only model-generated answers travel as raw text — those
 * are produced in the user's language because the request carries `languageCode`.
 *
 * Adding a reply: add the enum member here AND the `en` + `sv` entries under `assistant` in the
 * web-evolve locale files (`public/locales/en/translation.json` and the `sv` one), then bump
 * `defaultVersion` in both `transition-web-evolve/src/i18n.ts` and `intraction-web/src/i18n.ts`.
 * `assistantMessages.test.ts` fails if a key is missing from either locale.
 */

import { AI_COACH_VOICE_PRESETS } from './aiCoachVoicePresets'

/** Deterministic reply bodies. */
export enum AssistantMessageKey {
  CANNOT_HELP = 'assistant.messages.cannotHelp',
  /** Spoken refusals must not suggest pages: the caller asked for an answer and has no buttons. */
  NO_ANSWER_IN_YOUR_DATA = 'assistant.messages.noAnswerInYourData',
  /** The call itself failed (offline, transport error) — the only reply the client composes. */
  REQUEST_FAILED = 'assistant.messages.requestFailed',
  FACILITATOR_ONLY = 'assistant.messages.facilitatorOnly',
  INVALID_REQUEST = 'assistant.messages.invalidRequest',

  OPENING_SPRINT_CREATION = 'assistant.messages.openingSprintCreation',
  OPENING_SPRINT_MANAGEMENT = 'assistant.messages.openingSprintManagement',
  OPENING_REPORT_GENERATION = 'assistant.messages.openingReportGeneration',
  OPENING_REPORTS = 'assistant.messages.openingReports',
  OPENING_CONTROL_TOWER = 'assistant.messages.openingControlTower',
  OPENING_REFLECTIONS = 'assistant.messages.openingReflections',
  OPENING_NEW_REFLECTION = 'assistant.messages.openingNewReflection',
  OPENING_ORGANIZATION_MEMBERS = 'assistant.messages.openingOrganizationMembers',
  OPENING_UPCOMING_ACTIVITIES = 'assistant.messages.openingUpcomingActivities',
  OPENING_PROFILE = 'assistant.messages.openingProfile',
  OPENING_ACCOUNT_SETTINGS = 'assistant.messages.openingAccountSettings',
  OPENING_GAME_CENTER = 'assistant.messages.openingGameCenter',
  OPENING_ACHIEVEMENTS = 'assistant.messages.openingAchievements',
  OPENING_TEAM_ANALYSIS = 'assistant.messages.openingTeamAnalysis',
  OPENING_INDIVIDUAL_ANALYSIS = 'assistant.messages.openingIndividualAnalysis',
  OPENING_PRIVACY_NOTICE = 'assistant.messages.openingPrivacyNotice',
  OPENING_HELP = 'assistant.messages.openingHelp',
  OPENING_TUTORIALS = 'assistant.messages.openingTutorials',
  OPENING_AI_COACH = 'assistant.messages.openingAiCoach',

  ACHIEVEMENTS_ARE_IN_GAMER_AREA = 'assistant.messages.achievementsAreInGamerArea',
  TEAM_ANALYSIS_IS_IN_GAMER_AREA = 'assistant.messages.teamAnalysisIsInGamerArea',
  INDIVIDUAL_ANALYSIS_IS_IN_GAMER_AREA = 'assistant.messages.individualAnalysisIsInGamerArea',
  CAPABILITIES_FACILITATOR = 'assistant.messages.capabilitiesFacilitator',
  CAPABILITIES_GAMER = 'assistant.messages.capabilitiesGamer',
  /** Said alongside the Refresh card, so it describes a button rather than promising to press it. */
  REFRESH_MAY_HELP = 'assistant.messages.refreshMayHelp',

  /** params: `{{title}}` */
  LATEST_SPRINT_IS = 'assistant.messages.latestSprintIs',
  /** params: `{{title}}` */
  OPENING_SPRINT_EDIT = 'assistant.messages.openingSprintEdit',
  NO_SPRINT_ON_ACCOUNT = 'assistant.messages.noSprintOnAccount',
  NO_SPRINT_TO_EDIT = 'assistant.messages.noSprintToEdit',
  SPRINT_EDIT_FACILITATOR_ONLY = 'assistant.messages.sprintEditFacilitatorOnly',

  REPORTS_FACILITATOR_ONLY = 'assistant.messages.reportsFacilitatorOnly',
  REPORT_GENERATION_FACILITATOR_ONLY = 'assistant.messages.reportGenerationFacilitatorOnly',
  PARTICIPANT_SUMMARY_FACILITATOR_ONLY = 'assistant.messages.participantSummaryFacilitatorOnly',
  TEAM_PERFORMANCE_REPORT_ACCESS_ONLY = 'assistant.messages.teamPerformanceReportAccessOnly',
  SELF_FEEDBACK_PARTICIPANTS_ONLY = 'assistant.messages.selfFeedbackParticipantsOnly',

  NO_COMPLETED_SPRINT_FOR_REPORT = 'assistant.messages.noCompletedSprintForReport',
  NO_REPORT_ON_LATEST_SPRINT = 'assistant.messages.noReportOnLatestSprint',
  OPENING_LATEST_REPORT = 'assistant.messages.openingLatestReport',
  /** params: `{{title}}` */
  OPENING_REPORT_FOR_SPRINT = 'assistant.messages.openingReportForSprint',
  OPENING_REPORT_FOR_LATEST_SPRINT = 'assistant.messages.openingReportForLatestSprint',
  OPENING_REPORT_FOR_LATEST_TWO_SPRINTS = 'assistant.messages.openingReportForLatestTwoSprints',
  CHOOSE_COMPLETED_SPRINT = 'assistant.messages.chooseCompletedSprint',
  SPRINT_NOT_FOUND = 'assistant.messages.sprintNotFound',
  SPRINT_NOT_COMPLETED = 'assistant.messages.sprintNotCompleted',
  REPORT_NOT_ACCESSIBLE = 'assistant.messages.reportNotAccessible',
  NOT_ENOUGH_REPORT_TEXT = 'assistant.messages.notEnoughReportText',

  NO_PARTICIPANTS_ON_COMPLETED_SPRINT = 'assistant.messages.noParticipantsOnCompletedSprint',
  /** params: `{{title}}` */
  CHOOSE_PARTICIPANT_FROM_SPRINT = 'assistant.messages.chooseParticipantFromSprint',
  CHOOSE_PARTICIPANT_FIRST = 'assistant.messages.chooseParticipantFirst',
  PARTICIPANT_NOT_IN_LATEST_SPRINT = 'assistant.messages.participantNotInLatestSprint',
  NOT_ENOUGH_PARTICIPANT_FEEDBACK = 'assistant.messages.notEnoughParticipantFeedback',

  NOT_ENOUGH_FEEDBACK_TEXT = 'assistant.messages.notEnoughFeedbackText',
  LATEST_FEEDBACK_HERE = 'assistant.messages.latestFeedbackHere',
  LATEST_FEEDBACK_IN_REPORTS = 'assistant.messages.latestFeedbackInReports',

  LEADER_PEOPLE_RESULTS_NOT_READY = 'assistant.messages.leaderPeopleResultsNotReady',

  LIVE_VOICE_DISABLED = 'assistant.messages.liveVoiceDisabled',
  LIVE_VOICE_START_FAILED = 'assistant.messages.liveVoiceStartFailed',
  LIVE_VOICE_MICROPHONE_BLOCKED = 'assistant.messages.liveVoiceMicrophoneBlocked',
  LIVE_VOICE_RATE_LIMITED_MINUTE = 'assistant.messages.liveVoiceRateLimitedMinute',
  LIVE_VOICE_RATE_LIMITED_DAY = 'assistant.messages.liveVoiceRateLimitedDay',
  CHAT_RATE_LIMITED_MINUTE = 'assistant.messages.chatRateLimitedMinute',
  CHAT_RATE_LIMITED_DAY = 'assistant.messages.chatRateLimitedDay',
  ORGANIZATION_LIMIT_REACHED = 'assistant.messages.organizationLimitReached',
  ASSISTANT_OFF_FOR_ORGANIZATION = 'assistant.messages.assistantOffForOrganization'
}

/** Button/link labels on an action, and quick-reply chip labels. */
export enum AssistantActionLabelKey {
  CREATE_SPRINT = 'assistant.actions.createSprint',
  OPEN_SPRINTS = 'assistant.actions.openSprints',
  EDIT_SPRINT = 'assistant.actions.editSprint',
  GENERATE_REPORT = 'assistant.actions.generateReport',
  OPEN_REPORT = 'assistant.actions.openReport',
  OPEN_REPORTS = 'assistant.actions.openReports',
  OPEN_CONTROL_TOWER = 'assistant.actions.openControlTower',
  OPEN_REFLECTIONS = 'assistant.actions.openReflections',
  CREATE_REFLECTION = 'assistant.actions.createReflection',
  OPEN_ORGANIZATION_MEMBERS = 'assistant.actions.openOrganizationMembers',
  OPEN_ACTIVITIES = 'assistant.actions.openActivities',
  OPEN_PROFILE = 'assistant.actions.openProfile',
  OPEN_SETTINGS = 'assistant.actions.openSettings',
  OPEN_GAME_CENTER = 'assistant.actions.openGameCenter',
  OPEN_ACHIEVEMENTS = 'assistant.actions.openAchievements',
  OPEN_TEAM_ANALYSIS = 'assistant.actions.openTeamAnalysis',
  OPEN_INDIVIDUAL_ANALYSIS = 'assistant.actions.openIndividualAnalysis',
  OPEN_PRIVACY_NOTICE = 'assistant.actions.openPrivacyNotice',
  OPEN_HELP = 'assistant.actions.openHelp',
  OPEN_TUTORIALS = 'assistant.actions.openTutorials',
  OPEN_AI_COACH = 'assistant.actions.openAiCoach',
  OPEN_FEEDBACK = 'assistant.actions.openFeedback',

  QUICK_LATEST_SPRINT = 'assistant.quickActions.latestSprint',
  QUICK_EDIT_LATEST_SPRINT = 'assistant.quickActions.editLatestSprint',
  QUICK_SELECT_REPORT_SPRINT = 'assistant.quickActions.selectReportSprint',
  QUICK_SELECT_PARTICIPANT = 'assistant.quickActions.selectParticipant',
  QUICK_CONTROL_TOWER = 'assistant.quickActions.controlTower',
  QUICK_SELF_SESSION_PERFORMANCE = 'assistant.quickActions.selfSessionPerformance',
  QUICK_SELF_SESSION_NEXT_STEP = 'assistant.quickActions.selfSessionNextStep',
  QUICK_LATEST_FEEDBACK = 'assistant.quickActions.latestFeedback',
  QUICK_UPCOMING_ACTIVITIES = 'assistant.quickActions.upcomingActivities',
  // Chips built from real data: the translation is just the placeholder plus any decoration, so the
  // sprint title or participant name is never itself translated.
  /** params: `{{title}}` */
  QUICK_SPRINT_CHOICE = 'assistant.quickActions.sprintChoice',
  /** params: `{{title}}` — newest completed sprint, marked as such. */
  QUICK_SPRINT_CHOICE_LATEST = 'assistant.quickActions.sprintChoiceLatest',
  /** params: `{{name}}` */
  QUICK_PARTICIPANT_CHOICE = 'assistant.quickActions.participantChoice'
}

/** Short descriptions under a quick-reply chip. */
export enum AssistantActionDescriptionKey {
  COMPLETED_SPRINT = 'assistant.quickActions.descriptions.completedSprint'
}

/**
 * Deterministic replies from the 1:1 AI Coach. Separate enum because the coach is its own feature with
 * its own locale section, but the rule is the same: no handler returns English prose.
 */
export enum CoachMessageKey {
  LIVE_VOICE_DISABLED = 'aiCoach.messages.liveVoiceDisabled',
  LIVE_VOICE_RATE_LIMITED_MINUTE = 'aiCoach.messages.liveVoiceRateLimitedMinute',
  LIVE_VOICE_RATE_LIMITED_DAY = 'aiCoach.messages.liveVoiceRateLimitedDay',
  /** The organization has used its Aria requests for this month. */
  ORGANIZATION_LIMIT_REACHED = 'aiCoach.messages.organizationLimitReached',
  /** Repeated off-topic use: a warning, then a temporary pause. */
  OFF_TOPIC_WARNING = 'aiCoach.messages.offTopicWarning',
  OFF_TOPIC_PAUSED = 'aiCoach.messages.offTopicPaused',
  /** The model returned nothing usable for this turn. */
  EMPTY_REPLY = 'aiCoach.messages.emptyReply',
  /** Goals were agreed that could not be written: the active list is already full. */
  GOAL_LIMIT_REACHED = 'aiCoach.messages.goalLimitReached',
  /** Gentle "take a break" nudges; they never block the conversation. */
  REST_NUDGE = 'aiCoach.messages.restNudge',
  SOFT_MESSAGE_CAP = 'aiCoach.messages.softMessageCap'
}

/** Chrome around the conversation: panel, launcher, voice controls. Rendered by the client only. */
export enum AssistantUiKey {
  NAME = 'assistant.ui.name',
  /** Superscript badge in the wordmark: "Aria" + this. Rendered by AriaBrand, never inline. */
  BRAND_AI_BADGE = 'assistant.ui.brandAiBadge',
  /** Trailing word of the coach wordmark ("Coach"), so the brand is translatable as parts. */
  BRAND_COACH_SUFFIX = 'assistant.ui.brandCoachSuffix',
  LIVE_BADGE = 'assistant.ui.liveBadge',
  OPEN = 'assistant.ui.open',
  BUBBLE_TOOLTIP = 'assistant.ui.bubbleTooltip',
  CLOSE = 'assistant.ui.close',
  /** What she says beside the bubble on the latest session feedback, once per session. */
  LATEST_SESSION_GREETING = 'assistant.ui.latestSessionGreeting',
  GREETING_DISMISS = 'assistant.ui.greetingDismiss',
  /** Puts a dragged widget back in its default corner. */
  RESET_POSITION = 'assistant.ui.resetPosition',
  /** Shrinks the panel to a short chat without closing the conversation. */
  MINIMIZE = 'assistant.ui.minimize',
  EXPAND = 'assistant.ui.expand',
  INPUT_PLACEHOLDER = 'assistant.ui.inputPlaceholder',
  INPUT_PLACEHOLDER_SUPPORT = 'assistant.ui.inputPlaceholderSupport',
  SEND = 'assistant.ui.send',

  START_VOICE = 'assistant.ui.startVoice',
  START_VOICE_ARIA_LABEL = 'assistant.ui.startVoiceAriaLabel',
  STOP_VOICE = 'assistant.ui.stopVoice',
  END_VOICE = 'assistant.ui.endVoice',
  END_VOICE_ARIA_LABEL = 'assistant.ui.endVoiceAriaLabel',
  MUTE_MIC = 'assistant.ui.muteMic',
  UNMUTE_MIC = 'assistant.ui.unmuteMic',
  VOICE_UNAVAILABLE = 'assistant.ui.voiceUnavailable',
  VOICE_ALLOW_MIC = 'assistant.ui.voiceAllowMic',
  VOICE_CONNECTING = 'assistant.ui.voiceConnecting',
  VOICE_MUTED = 'assistant.ui.voiceMuted',
  VOICE_LISTENING = 'assistant.ui.voiceListening',
  VOICE_STARTING = 'assistant.ui.voiceStarting',
  VOICE_HEARING_YOU = 'assistant.ui.voiceHearingYou',
  VOICE_ARIA_SPEAKING = 'assistant.ui.voiceAriaSpeaking',
  VOICE_ARIA_THINKING = 'assistant.ui.voiceAriaThinking',
  /** A real person from the team, never Aria — and deliberately not the individual's name. */
  SUPPORT_AUTHOR = 'assistant.ui.supportAuthor',
  /** Banner state while a person has the thread: nobody has answered yet / they are on it. */
  SUPPORT_WAITING = 'assistant.ui.supportWaiting',
  SUPPORT_ACTIVE = 'assistant.ui.supportActive',
  /** Aria has the floor again while the request stays open, so the banner names her instead. */
  SUPPORT_ARIA_AUTHOR = 'assistant.ui.supportAriaAuthor',
  SUPPORT_STILL_OPEN = 'assistant.ui.supportStillOpen',
  /** Stepping between the person and the assistant without closing the request. */
  SUPPORT_ASK_ARIA = 'assistant.ui.supportAskAria',
  SUPPORT_BACK_TO_SUPPORT = 'assistant.ui.supportBackToSupport',
  /** The user's own word that the request is finished — the only thing that closes it from their side. */
  SUPPORT_MARK_RESOLVED = 'assistant.ui.supportMarkResolved',
  SUPPORT_RESOLVE_PROMPT = 'assistant.ui.supportResolvePrompt',
  SUPPORT_RESOLVE_YES = 'assistant.ui.supportResolveYes',
  SUPPORT_RESOLVE_NOT_YET = 'assistant.ui.supportResolveNotYet',

  MODE_VOICE = 'assistant.ui.modeVoice',
  MODE_VOICE_SUBTITLES = 'assistant.ui.modeVoiceSubtitles',
  MODE_TEXT = 'assistant.ui.modeText',
  MODE_GROUP_LABEL = 'assistant.ui.modeGroupLabel',
  LANGUAGE_PICKER_LABEL = 'assistant.ui.languagePickerLabel',
  VOICE_PRIMER_TITLE = 'assistant.ui.voicePrimerTitle',
  VOICE_PRIMER_BODY = 'assistant.ui.voicePrimerBody',
  VOICE_PRIMER_START = 'assistant.ui.voicePrimerStart',
  VOICE_PRIMER_TYPE_INSTEAD = 'assistant.ui.voicePrimerTypeInstead',

  ESCALATE = 'assistant.ui.escalate',
  ESCALATE_AGAIN = 'assistant.ui.escalateAgain',
  ESCALATE_SENT = 'assistant.ui.escalateSent',
  REFRESH_PROMPT = 'assistant.ui.refreshPrompt',
  REFRESH_NOW = 'assistant.ui.refreshNow',
  REFRESH_DISMISS = 'assistant.ui.refreshDismiss'
}

/** "Thinking…" lines shown while a chat answer is in flight. Cosmetic, but still user-facing copy. */
export enum AssistantLoadingPhraseKey {
  GENERAL_READING = 'assistant.loading.general.reading',
  GENERAL_CONTEXT = 'assistant.loading.general.context',
  GENERAL_ACTION = 'assistant.loading.general.action',
  GENERAL_PREPARING = 'assistant.loading.general.preparing',
  GENERAL_DETAILS = 'assistant.loading.general.details',
  GENERAL_PERMISSIONS = 'assistant.loading.general.permissions',

  FEEDBACK_LATEST = 'assistant.loading.feedback.latest',
  FEEDBACK_NOTES = 'assistant.loading.feedback.notes',
  FEEDBACK_TAKEAWAYS = 'assistant.loading.feedback.takeaways',
  FEEDBACK_NEXT_STEP = 'assistant.loading.feedback.nextStep',
  FEEDBACK_READY = 'assistant.loading.feedback.ready',

  REPORT_SPRINTS = 'assistant.loading.report.sprints',
  REPORT_DATA = 'assistant.loading.report.data',
  REPORT_OPTIONS = 'assistant.loading.report.options',
  REPORT_HIGHLIGHTS = 'assistant.loading.report.highlights',
  REPORT_PATTERNS = 'assistant.loading.report.patterns',

  PARTICIPANT_FINDING = 'assistant.loading.participant.finding',
  PARTICIPANT_AVAILABILITY = 'assistant.loading.participant.availability',
  PARTICIPANT_SIGNALS = 'assistant.loading.participant.signals',
  PARTICIPANT_SUMMARY = 'assistant.loading.participant.summary',
  PARTICIPANT_NEXT_STEPS = 'assistant.loading.participant.nextSteps',

  NAVIGATION_PAGE = 'assistant.loading.navigation.page',
  NAVIGATION_ACTION = 'assistant.loading.navigation.action',
  NAVIGATION_ROUTE = 'assistant.loading.navigation.route',
  NAVIGATION_AVAILABILITY = 'assistant.loading.navigation.availability',
  NAVIGATION_DESTINATION = 'assistant.loading.navigation.destination'
}

/** Machine-readable refusal cause. Never shown to the user — the message key carries the wording. */
export enum AssistantUnsupportedReason {
  NOT_UNDERSTOOD = 'not-understood',
  FACILITATOR_ONLY = 'facilitator-only',
  PARTICIPANT_ONLY = 'participant-only',
  INVALID_REQUEST = 'invalid-request',
  NOTHING_FOUND = 'nothing-found',
  NOT_READY_YET = 'not-ready-yet',
  RATE_LIMITED = 'rate-limited',
  FEATURE_DISABLED = 'feature-disabled'
}

export type AssistantTextParams = Record<string, string | number>

export enum AssistantMessageSource {
  /** Deterministic copy: the frontend translates `key` with `params`. */
  TRANSLATION = 'translation',
  /** Model-generated prose, already written in the user's language. */
  MODEL = 'model'
}

/** Any key the UI can translate: assistant copy or coach copy. */
export type TranslatableMessageKey = AssistantMessageKey | CoachMessageKey

export type AssistantMessage =
  | { source: AssistantMessageSource.TRANSLATION; key: TranslatableMessageKey; params?: AssistantTextParams }
  | { source: AssistantMessageSource.MODEL; text: string }

export type AssistantLabel = { key: AssistantActionLabelKey; params?: AssistantTextParams }

export function translatedMessage(key: TranslatableMessageKey, params?: AssistantTextParams): AssistantMessage {
  return params
    ? { source: AssistantMessageSource.TRANSLATION, key, params }
    : { source: AssistantMessageSource.TRANSLATION, key }
}

export function modelMessage(text: string): AssistantMessage {
  return { source: AssistantMessageSource.MODEL, text }
}

export function assistantLabel(key: AssistantActionLabelKey, params?: AssistantTextParams): AssistantLabel {
  return params ? { key, params } : { key }
}

/** Every translation key the assistant can emit — used by the locale-coverage test. */
export const ASSISTANT_TRANSLATION_KEYS: string[] = [
  ...Object.values(AssistantMessageKey),
  ...Object.values(CoachMessageKey),
  ...Object.values(AssistantActionLabelKey),
  ...Object.values(AssistantActionDescriptionKey),
  ...Object.values(AssistantUiKey),
  ...Object.values(AssistantLoadingPhraseKey),
  // Voice-preset copy lives in a data structure, so `yarn check-translations` (which scans for literal
  // `t('…')` calls) cannot see it. Listing it here puts it under the same locale test as the rest.
  ...AI_COACH_VOICE_PRESETS.flatMap((preset) => [preset.labelKey, preset.descriptionKey])
]
